/**
 * draftStorage — low-level localStorage helpers for T2-B.next.B unsaved-form
 * recovery.  All reads/writes to `localStorage['lextrack_draft:*']` go
 * through here so we have exactly one place that handles serialization,
 * expiry, build-id-scoped validity, and quota errors.
 *
 * Design after T2-B.next.B pre-code panel review (4-model consensus):
 * primary durability is DEBOUNCED WRITE-THROUGH on every dirty state
 * change (see useDraft.js).  Registry-flush at chunk-crash time is a
 * defensive backup only — the panel unanimously flagged that fighting
 * React 18 unmount ordering (componentDidCatch fires AFTER subtree
 * unmounts) is a dead-end approach.
 *
 * Key format: `lextrack_draft:<saveKey>`.  Value: JSON with
 *   {state, savedAt, buildId, originBuildId, windowInstance}
 * — savedAt is Date.now() at write time (used for TTL + banner UI)
 * — buildId is REACT_APP_BUILD_ID at the LAST write
 * — originBuildId is the build the stored STATE came from.  Normally the
 *   same thing; they diverge only after a user restores a cross-build
 *   draft, because restore spreads the old object into setState wholesale
 *   (stale keys and all) and the next write would otherwise re-stamp it
 *   with the current build.  `buildIdMismatch` is computed from the ORIGIN
 *   so the red "app was updated" banner survives that round trip instead of
 *   quietly going amber on a second crash.
 * — windowInstance is a per-Electron-window UUID stored in sessionStorage
 *   (per-renderer even under the shared persist:lextrack partition, so it
 *   distinguishes the main window from a pop-out).  It is CONSUMED, not just
 *   diagnostic: see isForeignWindowDraft below.  Pop-out windows share
 *   localStorage, so two windows editing the same row write the same key and
 *   the loser's draft is overwritten.  Callers that would otherwise apply a
 *   draft silently use the predicate to make the cross-window case opt-in.
 */

const KEY_PREFIX = 'lextrack_draft:'
const TTL_MS = 24 * 60 * 60 * 1000 // 24h

// Captured at MODULE LOAD, not inside functions — protects against the
// panel-flagged case (Kimi + Opus 4.8) where `process.env` is undefined
// in some Electron contexts (preload, injected script).  Falls back to
// 'unknown' rather than throwing at write time and blocking the reload.
const CURRENT_BUILD_ID =
  (typeof process !== 'undefined' && process.env && process.env.REACT_APP_BUILD_ID) ||
  'unknown'

// Per-BrowserWindow instance ID persisted to sessionStorage (which is
// per-renderer in Electron even with a shared partition).  Included in
// every draft payload so we can diagnose multi-window collisions in the
// stored data even though saveKey overwrites are last-writer-wins.
const WINDOW_INSTANCE_ID = (() => {
  try {
    let id = sessionStorage.getItem('lextrack_window_instance_id')
    if (!id) {
      const rand =
        (typeof crypto !== 'undefined' && crypto.randomUUID && crypto.randomUUID()) ||
        `${Date.now()}-${Math.random().toString(36).slice(2)}`
      id = String(rand)
      sessionStorage.setItem('lextrack_window_instance_id', id)
    }
    return id
  } catch (e) {
    return 'unknown'
  }
})()

/**
 * carriedOrigin — the origin build of the draft ALREADY stored under this
 * key, but only when that draft is flagged as a cross-build restoration
 * (its origin differs from whichever build last wrote it).
 *
 * This exists because two of the four writeDraft call sites cannot pass an
 * origin: draftFlushRegistry's unmount flush and its flushAllDrafts, which
 * is what ChunkLoadErrorBoundary calls immediately before reload.  That is
 * the very step the double-crash scenario is made of, so without this the
 * threading through useDraft would be laundered right where it matters.
 *
 * Narrowing it to already-flagged entries is what keeps it honest: a draft
 * whose origin equals its buildId is native, so a fresh edit under a new
 * build gets the current build stamped exactly as it does today.  Lineage
 * belongs to the state object, not to the storage slot.
 *
 * Own try/catch, deliberately: writeDraft's outer catch returns false and
 * SKIPS the write, so a malformed neighbouring payload must not be able to
 * block a legitimate save.
 */
function carriedOrigin (saveKey) {
  try {
    const raw = localStorage.getItem(KEY_PREFIX + saveKey)
    if (!raw) return null
    const p = JSON.parse(raw)
    if (!p || typeof p !== 'object') return null
    const origin = p.originBuildId
    if (!origin) return null // pre-fix payload — treat as native
    if (origin === (p.buildId || 'unknown')) return null // native, not restored
    return origin
  } catch (e) {
    return null
  }
}

/**
 * writeDraft — synchronous JSON+setItem.  Never throws — returns
 * boolean success so callers can decide whether to warn.  Panel finding:
 * setItem can throw QuotaExceededError / SecurityError / JSON errors on
 * BigInt / circular refs — one bad draft cannot block the reload path.
 *
 * @param opts.originBuildId — the build the state came from, when the caller
 *        knows it (useDraft passes it after a restore).  Omitted by the two
 *        registry call sites, which is what carriedOrigin above covers.
 */
export function writeDraft (saveKey, state, opts = {}) {
  if (!saveKey || state == null) return false
  try {
    const payload = JSON.stringify({
      state,
      savedAt: Date.now(),
      buildId: CURRENT_BUILD_ID,
      originBuildId:
        (opts && opts.originBuildId) || carriedOrigin(saveKey) || CURRENT_BUILD_ID,
      windowInstance: WINDOW_INSTANCE_ID
    })
    localStorage.setItem(KEY_PREFIX + saveKey, payload)
    return true
  } catch (e) {
    return false
  }
}

/**
 * readDraft — parses + validates.  Returns null when: absent, malformed,
 * expired, or missing savedAt.  Returns object with `buildIdMismatch: true`
 * when the draft was written under a DIFFERENT build than currently loaded
 * — caller must decide whether to warn the user (form fields may have
 * changed between builds).
 *
 * Panel finding: read-time is also cleanup time.  Any malformed/expired
 * entry gets removed on this read so localStorage doesn't accumulate.
 */
export function readDraft (saveKey) {
  if (!saveKey) return null
  let raw
  try {
    raw = localStorage.getItem(KEY_PREFIX + saveKey)
  } catch (e) {
    return null
  }
  if (!raw) return null
  let parsed
  try {
    parsed = JSON.parse(raw)
  } catch (e) {
    try { localStorage.removeItem(KEY_PREFIX + saveKey) } catch (_) {}
    return null
  }
  if (!parsed || typeof parsed !== 'object' || typeof parsed.savedAt !== 'number') {
    try { localStorage.removeItem(KEY_PREFIX + saveKey) } catch (_) {}
    return null
  }
  // Post-code panel (Kimi + Opus 4.8): a client with skewed clock writes a
  // draft with a "future" savedAt (from its perspective); after NTP or
  // manual clock correction the same client reads it back and would
  // discard as malformed — losing the draft precisely during a clock-
  // correction event.  Instead: on future timestamps, treat as if written
  // "just now" (clamp effective savedAt to Date.now()) so expiry still
  // works and the draft isn't lost.
  const effectiveSavedAt = Math.min(parsed.savedAt, Date.now())
  if (Date.now() - effectiveSavedAt > TTL_MS) {
    try { localStorage.removeItem(KEY_PREFIX + saveKey) } catch (_) {}
    return null
  }
  // Drafts already sitting in a user's localStorage have no originBuildId.
  // The `|| parsed.buildId` fallback is what keeps them reading exactly as
  // they do today; drop it and every pre-existing draft reports origin
  // 'unknown' and goes permanently red.  Do not simplify this chain.
  const originBuildId = parsed.originBuildId || parsed.buildId || 'unknown'
  return {
    state: parsed.state,
    savedAt: parsed.savedAt,
    buildId: parsed.buildId || 'unknown',
    originBuildId,
    buildIdMismatch: originBuildId !== CURRENT_BUILD_ID,
    windowInstance: parsed.windowInstance || 'unknown'
  }
}

export function clearDraft (saveKey) {
  if (!saveKey) return
  try { localStorage.removeItem(KEY_PREFIX + saveKey) } catch (e) {}
}

/**
 * sweepExpiredDrafts — boot-time cleanup of `lextrack_draft:*` keys.
 * Panel finding: read-time expiry alone leaves orphan keys for saveKeys
 * that are never reopened (deleted invoices, ephemeral flows).  Sweep
 * once at app startup to keep localStorage bounded.
 *
 * Iterates DESCENDING so removeItem doesn't shift the index sequence.
 */
export function sweepExpiredDrafts () {
  try {
    const now = Date.now()
    for (let i = localStorage.length - 1; i >= 0; i--) {
      let k
      try {
        k = localStorage.key(i)
      } catch (e) {
        continue
      }
      if (!k || !k.startsWith(KEY_PREFIX)) continue
      let raw, parsed
      try {
        raw = localStorage.getItem(k)
      } catch (e) {
        continue
      }
      if (!raw) {
        try { localStorage.removeItem(k) } catch (_) {}
        continue
      }
      try {
        parsed = JSON.parse(raw)
      } catch (e) {
        try { localStorage.removeItem(k) } catch (_) {}
        continue
      }
      if (
        !parsed ||
        typeof parsed !== 'object' ||
        typeof parsed.savedAt !== 'number' ||
        now - Math.min(parsed.savedAt, now) > TTL_MS
      ) {
        // Only truly-expired or malformed entries deleted here — future
        // timestamps are clamped (see readDraft comment) rather than
        // discarded, to survive clock-correction events.
        try { localStorage.removeItem(k) } catch (_) {}
      }
    }
  } catch (e) {
    // Whole sweep best-effort — don't crash app boot if storage is broken
  }
}

/**
 * isForeignWindowDraft — true when this draft was demonstrably written by a
 * DIFFERENT window than the one asking.
 *
 * Lives here rather than at the two call sites (TimeEntries' silent-restore
 * branch, DraftRestoreBanner's provenance note) so the comparison cannot
 * drift between them.
 *
 * Deliberately FALSE when either side is the 'unknown' sentinel.  'unknown'
 * means "we could not read sessionStorage", not "a different window" — and
 * this file must not assert provenance it does not have.  Returning false
 * there also means every ambiguous case falls back to the behaviour that
 * shipped before this predicate existed, so a hardened context that blocks
 * sessionStorage degrades toward today rather than away from it.
 */
export function isForeignWindowDraft (draft) {
  if (!draft) return false
  const written = draft.windowInstance
  if (!written || written === 'unknown') return false
  if (!WINDOW_INSTANCE_ID || WINDOW_INSTANCE_ID === 'unknown') return false
  return written !== WINDOW_INSTANCE_ID
}

// Exported for diagnostic use only.  Do not include these in normal
// code paths — callers should use readDraft/writeDraft/clearDraft, or
// isForeignWindowDraft for window provenance.
export const __CURRENT_BUILD_ID = CURRENT_BUILD_ID
export const __WINDOW_INSTANCE_ID = WINDOW_INSTANCE_ID
